import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';

const VIDEO_URL = 'https://cloud.sharemyimage.com/2026/09/17/Statement-Sync-Pro---Extract-Transactions-under-30-seconds.mp4';

function DemoSection() {
  const reducedMotion = useReducedMotion();

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <section id="demo" aria-labelledby="demo-heading" className="relative overflow-hidden bg-slate-950 py-20 text-white sm:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,_rgba(37,99,235,0.25),_transparent_30%),radial-gradient(circle_at_80%_70%,_rgba(16,185,129,0.18),_transparent_28%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: reducedMotion ? 0 : 0.55 }}
          className="mx-auto mb-10 max-w-3xl text-center sm:mb-14"
        >
          <span className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">4-minute product walkthrough</span>
          <h2 id="demo-heading" className="mt-4 text-3xl font-black tracking-tight sm:text-5xl">Want to see exactly how StatementSync works?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-300 sm:text-lg">
            Watch the complete workflow from a readable bank statement PDF to reviewed, spreadsheet-ready Excel or CSV data before you choose a plan.
          </p>
        </motion.div>

        <motion.div
          initial={reducedMotion ? false : { opacity: 0, scale: 0.985, y: 16 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, amount: 0.16 }}
          transition={{ duration: reducedMotion ? 0 : 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto max-w-6xl"
        >
          <div className="absolute -inset-8 rounded-[3rem] bg-gradient-to-r from-blue-500/20 via-cyan-400/10 to-emerald-400/20 blur-3xl" />

          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900/90 p-2 shadow-[0_32px_100px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-3">
            <svg
              className="pointer-events-none absolute inset-0 z-20 h-full w-full"
              viewBox="0 0 1000 620"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="demo-tracer" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="45%" stopColor="#22d3ee" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
                <filter id="demo-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <rect x="1.5" y="1.5" width="997" height="617" rx="30" fill="none" stroke="rgba(148,163,184,0.24)" strokeWidth="2" />

              {!reducedMotion && (
                <>
                  <motion.rect
                    x="1.5"
                    y="1.5"
                    width="997"
                    height="617"
                    rx="30"
                    fill="none"
                    stroke="url(#demo-tracer)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    pathLength="1"
                    strokeDasharray="0.11 0.89"
                    animate={{ strokeDashoffset: [0, -1] }}
                    transition={{ duration: 5.5, repeat: Infinity, ease: 'linear' }}
                  />
                  <motion.rect
                    x="1.5"
                    y="1.5"
                    width="997"
                    height="617"
                    rx="30"
                    fill="none"
                    stroke="url(#demo-tracer)"
                    strokeWidth="7"
                    strokeLinecap="round"
                    pathLength="1"
                    strokeDasharray="0.035 0.965"
                    filter="url(#demo-glow)"
                    opacity="0.85"
                    animate={{ strokeDashoffset: [0, -1] }}
                    transition={{ duration: 5.5, repeat: Infinity, ease: 'linear' }}
                  />
                </>
              )}
            </svg>

            <div className="relative z-10 overflow-hidden rounded-[1.55rem] border border-white/10 bg-slate-950">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-5">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="text-center">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-300 sm:text-xs">StatementSync real workflow</p>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold text-slate-300 sm:text-xs">~4 min</div>
              </div>

              <div className="relative aspect-video bg-black">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full bg-black object-contain"
                  aria-label="StatementSync product walkthrough — PDF bank statement to Excel and CSV"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                  Your browser does not support embedded MP4 video.{' '}
                  <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer">Open the StatementSync demo video.</a>
                </video>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mx-auto mt-8 grid max-w-4xl gap-3 text-center sm:grid-cols-3">
          {[
            ['See the real workflow', 'Follow the process from PDF upload to structured transaction rows.'],
            ['See the review step', 'Watch how extracted data can be checked before export.'],
            ['See the final output', 'See how the cleaned data is prepared for Excel or CSV.'],
          ].map(([title, copy]) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 backdrop-blur-sm">
              <p className="font-bold text-white">{title}</p>
              <p className="mt-1 text-sm leading-6 text-slate-400">{copy}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={scrollToPricing}
            className="inline-flex items-center justify-center rounded-2xl bg-white px-6 py-3.5 text-sm font-bold text-slate-950 shadow-xl shadow-black/20 transition hover:-translate-y-0.5 hover:bg-cyan-50"
          >
            See pricing after the demo
            <span aria-hidden="true" className="ml-2">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

export default function VideoDemoPortal() {
  const [host, setHost] = useState(null);

  useEffect(() => {
    const main = document.querySelector('main#top');
    if (!main) return undefined;

    const sections = Array.from(main.children).filter((node) => node.tagName === 'SECTION');
    const proofStrip = sections[1];
    if (!proofStrip) return undefined;

    const existing = document.getElementById('statement-sync-video-demo-host');
    if (existing) {
      setHost(existing);
      return undefined;
    }

    const portalHost = document.createElement('div');
    portalHost.id = 'statement-sync-video-demo-host';
    proofStrip.insertAdjacentElement('afterend', portalHost);
    setHost(portalHost);

    return () => {
      portalHost.remove();
      setHost(null);
    };
  }, []);

  return host ? createPortal(<DemoSection />, host) : null;
}